/**
 * Programa de influenciadores (spec 038).
 *
 * Contrato comercial com quem traz assinante do Kardume Pass: valor em real, por
 * plano, negociado pessoa a pessoa. Não confundir com o "Convide um amigo", que
 * premia com pontos e não tem nada aqui - os dois convivem no mesmo indicado.
 */

/**
 * Ciclo de vida de uma comissão.
 *
 * `PENDING` -> `APPROVED` (entrou num fechamento) -> `PAID`, ou qualquer um
 * deles -> `CANCELED`. `PAID` é terminal: comissão paga não volta.
 */
export type CommissionStatus = 'PENDING' | 'APPROVED' | 'PAID' | 'CANCELED';

/** Situação de um fechamento de pagamento. */
export type PayoutStatus = 'OPEN' | 'PAID' | 'CANCELED';

/** Uma linha da tabela de valores: este plano paga tanto a este influenciador. */
export interface InfluencerRateDto {
  id: string;
  planId: string;
  planName: string;
  planPrice: number;
  amount: number;
  active: boolean;
}

/** Um contrato de influenciador, como o backoffice o vê. */
export interface InfluencerDto {
  id: string;
  createdAt: string;
  customerId: string;
  customerName: string;
  customerEmail?: string | null;
  customerPhone?: string | null;

  /** O código de afiliado que a pessoa já tinha - o mesmo de todo cliente. */
  affiliateCode?: string | null;
  inviteLink?: string | null;

  active: boolean;
  /** `true` = cada renovação paga comissiona de novo. Negociado por pessoa. */
  recurringCommission: boolean;
  payoutNotes?: string | null;
  notes?: string | null;
  startedAt: string;
  endedAt?: string | null;

  rates: InfluencerRateDto[];

  pendingAmount: number;
  approvedAmount: number;
  paidAmount: number;
  convertedCount: number;
}

export interface CreateInfluencerRequestDto {
  customerId: string;
  recurringCommission: boolean;
  payoutNotes?: string;
  notes?: string;
}

export interface UpdateInfluencerRequestDto {
  active: boolean;
  recurringCommission: boolean;
  payoutNotes?: string;
  notes?: string;
}

/**
 * Definir quanto um plano paga. `amount` é sempre maior que zero: zero não é
 * acordo, é ausência de acordo - e ausência se diz removendo a linha.
 */
export interface InfluencerRateRequestDto {
  planId: string;
  amount: number;
  active: boolean;
}

/** Uma comissão no ledger do backoffice (aqui o indicado aparece). */
export interface CommissionDto {
  id: string;
  influencerId: string;
  influencerName: string;
  referredCustomerId?: string | null;
  referredCustomerName?: string | null;
  planName: string;
  amount: number;
  /** O valor da cobrança que originou - contexto para conferir. */
  baseAmount: number;
  status: CommissionStatus;
  /** Competência `AAAA-MM`. */
  competence: string;
  payoutId?: string | null;
  canceledReason?: string | null;
  occurredAt: string;
}

/** Um fechamento: lote de comissões de uma competência, com o total congelado. */
export interface PayoutDto {
  id: string;
  influencerId: string;
  influencerName: string;
  competence: string;
  totalAmount: number;
  commissionCount: number;
  status: PayoutStatus;
  paidAt?: string | null;
  paymentReference?: string | null;
  notes?: string | null;
  createdAt: string;
}

export interface InfluencerOverviewDto {
  activeInfluencers: number;
  totalInfluencers: number;
  pendingAmount: number;
  approvedAmount: number;
  paidAmount: number;
  commissionsThisMonth: number;
  amountThisMonth: number;
}

/** Rótulos das situações, na linguagem da operação. */
export const COMMISSION_STATUS_LABELS: Record<CommissionStatus, string> = {
  PENDING: 'A conferir',
  APPROVED: 'A pagar',
  PAID: 'Paga',
  CANCELED: 'Cancelada',
};

export const PAYOUT_STATUS_LABELS: Record<PayoutStatus, string> = {
  OPEN: 'Em aberto',
  PAID: 'Pago',
  CANCELED: 'Cancelado',
};
